/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import type { ComponentNode, TaskExecutionController, ParsedParams } from '@karya/generic-task-react';
import type { GenericTask } from '@karya/core';

/**
 * Base props for all Chakra UI components in the task renderer
 */
export interface ComponentRendererProps {
  /** The parsed component node containing component and params */
  node: ComponentNode<GenericTask[string]>;
  /** The task execution controller for navigation and output. Null for completed assignments. */
  controller: TaskExecutionController | null;
  /** Whether this is the current active component */
  isCurrent: boolean;
}

/**
 * Configuration for mapping dtype to Chakra component
 */
export type ComponentRendererConfig = {
  [dtype: string]: React.ComponentType<ComponentRendererProps>;
};

/**
 * Helper function to get typed params from a component node.
 * This eliminates the need for manual type casting in components.
 *
 * @param node - The component node containing params
 * @returns Properly typed params for the component
 */
export function getTypedParams<ComponentType extends GenericTask[string]>(
  node: ComponentNode<GenericTask[string]>,
): ParsedParams<ComponentType> {
  return node.params as unknown as ParsedParams<ComponentType>;
}

/**
 * Helper function to get typed component from a component node.
 * This eliminates the need for manual type casting when accessing component properties.
 *
 * @param node - The component node containing the component
 * @returns Properly typed component
 */
export function getTypedComponent<ComponentType extends GenericTask[string]>(
  node: ComponentNode<GenericTask[string]>,
): ComponentType {
  return node.component as unknown as ComponentType;
}
